import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "./Sidebar";
import "./ImsDashboard.css";

// Chart.js imports
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ─── Config ───────────────────────────────────────────────────────────────────
const API_BASE = "https://mhsdev.online/MHS-WEB/api/inventory.php";
const api = (action, params = "") =>
  `${API_BASE}?action=${action}${params ? "&" + params : ""}`;

const CLASSES = [
  "Play Group", "Nursery", "Prep", "Class 1", "Class 2", "Class 3",
  "Class 4", "Class 5", "Class 6", "Class 7", "Class 8",
  "Class Pre 9", "Class 9", "Class 10"
];

const ICON_OPTIONS = [
  { value: "book", label: "📚 Book" },
  { value: "shirt", label: "👕 Uniform" },
  { value: "pen", label: "🖊️ Stationery" },
  { value: "bag", label: "🎒 Bag" },
  { value: "shoe", label: "👟 Shoe" },
  { value: "other", label: "📦 Other" },
];

const CAT_COLORS = [
  "#4f46e5","#0ea5e9","#f59e0b","#ef4444",
  "#8b5cf6","#ec4899","#14b8a6","#f97316",
];

const LOW_STOCK_THRESHOLD = 5; // total quantity (pairs+singles) below this triggers alert

const generateKey = () => Math.random().toString(36).substr(2, 6);
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" }) : "—";

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d;
  if (diff < 3600000)  return `${Math.floor(diff/60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
  return d.toLocaleDateString("en-PK");
};

async function apiFetch(action, method = "GET", body = null, queryParams = "") {
  const url  = api(action, queryParams);
  const opts = { method, headers: { "Content-Type": "application/json" } };
  if (body) opts.body = JSON.stringify(body);
  const res  = await fetch(url, opts);
  const data = await res.json();
  if (!res.ok || data.error) throw new Error(data.error || "Request failed");
  return data;
}

// ─── UI components ────────────────────────────────────────────────────────────
const Spinner = ({ dark }) => <span className={`ims-spinner${dark ? " ims-spinner--dark" : ""}`} />;

const Toast = ({ msg, type }) => (
  <div className={`ims-toast ims-toast--${type}`}>
    {type === "success" ? "✅" : type === "error" ? "❌" : "ℹ️"} {msg}
  </div>
);

const Toggle = ({ checked, onChange, label }) => (
  <div className="ims-toggle-wrap">
    <label className="ims-toggle-switch">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="ims-toggle-slider" />
    </label>
    {label && <span className="ims-toggle-label">{label}</span>}
  </div>
);

const Field = ({ label, required, optional, children }) => (
  <div className="ims-field">
    <label className="ims-label">
      {label}
      {required && <span className="ims-req"> *</span>}
      {optional && <span className="ims-opt">optional</span>}
    </label>
    {children}
  </div>
);

const SecTitle = ({ children }) => <div className="ims-sec-title">{children}</div>;

// ─── Icon picker ──────────────────────────────────────────────────────────────
const IconPicker = ({ value, onChange }) => (
  <div className="ims-chip-row">
    {ICON_OPTIONS.map(ico => (
      <button
        key={ico.value}
        className={`ims-chip ${value === ico.value ? "ims-chip--on" : ""}`}
        onClick={() => onChange(ico.value)}
      >
        {ico.label}
      </button>
    ))}
  </div>
);

// ─── Classes picker ───────────────────────────────────────────────────────────
const ClassesPicker = ({ selected, onChange }) => {
  const allSel = selected.length === CLASSES.length;
  const toggle = cl => onChange(selected.includes(cl) ? selected.filter(c => c !== cl) : [...selected, cl]);
  return (
    <div className="ims-form-section">
      <SecTitle>Applicable Classes</SecTitle>
      <div className="ims-classes-controls">
        <button
          className={`ims-btn ims-btn--xs ${allSel ? "ims-btn--red" : "ims-btn--primary"}`}
          onClick={() => onChange(allSel ? [] : [...CLASSES])}
        >{allSel ? "Deselect All" : "Select All"}</button>
        <span className="ims-count-label">{selected.length} selected</span>
      </div>
      <div className="ims-classes-grid">
        {CLASSES.map(cl => (
          <label key={cl} className={`ims-class-check ${selected.includes(cl) ? "ims-class-check--on" : ""}`}>
            <input type="checkbox" checked={selected.includes(cl)} onChange={() => toggle(cl)} />
            {cl}
          </label>
        ))}
      </div>
    </div>
  );
};

// ─── Modal wrapper ────────────────────────────────────────────────────────────
const ModalWrap = ({ size = "", accentColor = "", title, subtitle, footer, onClose, children }) => (
  <div className="ims-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className={`ims-modal ${size}`}>
      {accentColor && <div className="ims-modal-accent" style={{ background: accentColor }} />}
      <div className="ims-modal-head">
        <div>
          <div className="ims-modal-title">{title}</div>
          {subtitle && <div className="ims-modal-sub">{subtitle}</div>}
        </div>
        <button className="ims-modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="ims-modal-body">{children}</div>
      {footer && <div className="ims-modal-foot">{footer}</div>}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// CREATE CATEGORY MODAL
// ═══════════════════════════════════════════════════════════════════════════════
const CreateCategoryModal = ({ onClose, onDone, showToast }) => {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [iconName, setIconName] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]);

  const handleSubmit = async () => {
    if (!name.trim()) return showToast("Category name is required", "error");
    if (!selectedClasses.length) return showToast("Select at least one class", "error");

    try {
      setSaving(true);
      await apiFetch("create-category", "POST", {
        name: name.trim(),
        icon_name: iconName || null,
        classes: selectedClasses,
        created_by: "admin"
      });
      onDone();
    } catch (e) {
      showToast(e.message, "error");
    } finally { setSaving(false); }
  };

  return (
    <ModalWrap title="Create New Category" subtitle="Set name, icon, and applicable classes"
      onClose={onClose}
      footer={
        <>
          <button className="ims-btn ims-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="ims-btn ims-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <><Spinner /> Saving…</> : "Create Category"}
          </button>
        </>
      }
    >
      <div className="ims-form-section">
        <SecTitle>Basic Information</SecTitle>
        <Field label="Category Name" required>
          <input className="ims-input" placeholder="e.g., Uniforms, Books…"
            value={name} onChange={e => setName(e.target.value)} />
        </Field>
        <Field label="Icon" optional>
          <IconPicker value={iconName} onChange={setIconName} />
        </Field>
      </div>
      <ClassesPicker selected={selectedClasses} onChange={setSelectedClasses} />
    </ModalWrap>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT CATEGORY MODAL
// ═══════════════════════════════════════════════════════════════════════════════
const EditCategoryModal = ({ cat, onClose, onDone, showToast }) => {
  const isFixed = !!cat.is_fixed;
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(cat.name);
  const [iconName, setIconName] = useState(cat.icon_name || "");
  const [selectedClasses, setSelectedClasses] = useState(cat.classes || []);

  const handleSubmit = async () => {
    if (!name.trim()) return showToast("Name is required", "error");
    if (!selectedClasses.length) return showToast("Select at least one class", "error");

    try {
      setSaving(true);
      await apiFetch("update-category", "POST", {
        id: cat.id,
        name: name.trim(),
        icon_name: iconName || null,
        classes: selectedClasses,
        updated_by: "admin"
      });
      onDone();
    } catch (e) {
      showToast(e.message, "error");
    } finally { setSaving(false); }
  };

  return (
    <ModalWrap title="Edit Category" subtitle={`Updating "${cat.name}"`} onClose={onClose}
      footer={
        <>
          <button className="ims-btn ims-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="ims-btn ims-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <><Spinner /> Saving…</> : "Update Category"}
          </button>
        </>
      }
    >
      {isFixed && (
        <div className="ims-warn-box">
          ⚠️ <strong>Fixed category</strong> – its name cannot be changed.
        </div>
      )}

      <div className="ims-form-section">
        <SecTitle>Basic Information</SecTitle>
        <Field label="Category Name">
          <input className="ims-input" value={name} onChange={e => setName(e.target.value)} disabled={isFixed} />
        </Field>
        <Field label="Icon">
          <IconPicker value={iconName} onChange={setIconName} />
        </Field>
      </div>
      <ClassesPicker selected={selectedClasses} onChange={setSelectedClasses} />
    </ModalWrap>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DELETE MODAL (for categories)
// ═══════════════════════════════════════════════════════════════════════════════
const DeleteModal = ({ cat, onClose, onDone, showToast }) => {
  const [deleting, setDeleting] = useState(false);
  const hasItems = parseInt(cat.items_count || 0) > 0;

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await apiFetch("delete-category", "POST", { id: cat.id, deleted_by: "admin" });
      onDone();
    } catch (e) {
      showToast(e.message, "error");
    } finally { setDeleting(false); }
  };

  return (
    <div className="ims-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ims-modal ims-modal--sm">
        <div className="ims-confirm">
          <div className="ims-confirm-icon">{hasItems ? "⚠️" : "🗑️"}</div>
          <div className="ims-confirm-title">
            {hasItems ? "Cannot Delete" : `Delete "${cat.name}"?`}
          </div>
          <div className="ims-confirm-body">
            {hasItems
              ? `"${cat.name}" has ${cat.items_count} items. Remove them first.`
              : "This action is permanent and cannot be undone."}
          </div>
          <div className="ims-confirm-btns">
            <button className="ims-btn ims-btn--ghost" onClick={onClose}>Cancel</button>
            {!hasItems && (
              <button className="ims-btn ims-btn--red" onClick={handleDelete} disabled={deleting}>
                {deleting ? <><Spinner /> Deleting…</> : "Yes, Delete"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DELETE ITEM MODAL (used inside Warehouse)
// ═══════════════════════════════════════════════════════════════════════════════
const DeleteItemModal = ({ item, onClose, onDone, showToast }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await apiFetch("delete-item", "POST", { id: item.id, deleted_by: "admin" });
      showToast(`Item "${item.item_name}" deleted.`, "success");
      onDone();
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="ims-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ims-modal ims-modal--sm">
        <div className="ims-confirm">
          <div className="ims-confirm-icon">🗑️</div>
          <div className="ims-confirm-title">Delete "{item.item_name}"?</div>
          <div className="ims-confirm-body">
            This action is permanent and cannot be undone.
          </div>
          <div className="ims-confirm-btns">
            <button className="ims-btn ims-btn--ghost" onClick={onClose}>Cancel</button>
            <button className="ims-btn ims-btn--red" onClick={handleDelete} disabled={deleting}>
              {deleting ? <><Spinner /> Deleting…</> : "Yes, Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADJUST STOCK MODAL (for Add/Remove in Warehouse)
// ═══════════════════════════════════════════════════════════════════════════════
const AdjustStockModal = ({ item, cat, type, onClose, onDone, showToast }) => {
  const [quantity, setQuantity] = useState(1);
  const [reference, setReference] = useState("");
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!quantity || quantity <= 0) {
      return showToast("Please enter a valid positive quantity", "error");
    }

    setSaving(true);
    try {
      const payload = {
        item_id: item.id,
        entry_type: type,
        reference_details: reference || `Warehouse adjustment for ${cat.name}`,
        authorized_by: authorizedBy || "admin",
        created_by: "admin"
      };
      if (item.item_type === 'bundle') {
        payload.bundle_quantity = quantity;
      } else {
        payload.singles_changed = quantity;
        payload.pairs_changed = 0;
      }
      await apiFetch("adjust-stock", "POST", payload);
      showToast(`Stock ${type === "IN" ? "added" : "removed"} successfully!`, "success");
      onDone();
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrap size="ims-modal--sm"
      title={`${type === "IN" ? "Add" : "Remove"} Stock — ${item.item_name}`}
      subtitle={`Current stock: ${item.item_type === 'bundle' ? (item.current_bundle_qty ?? 0) : (item.current_single_qty ?? 0)}`}
      onClose={onClose}
      footer={
        <>
          <button className="ims-btn ims-btn--ghost" onClick={onClose}>Cancel</button>
          <button
            className={`ims-btn ${type === "IN" ? "ims-btn--success" : "ims-btn--warning"}`}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? <><Spinner /> Processing…</> : `${type === "IN" ? "Add" : "Remove"}`}
          </button>
        </>
      }
    >
      <div className="ims-form-section">
        <Field label="Quantity" required>
          <input
            className="ims-input"
            type="number"
            min="1"
            value={quantity}
            onChange={e => setQuantity(parseInt(e.target.value) || 1)}
          />
        </Field>
        <Field label="Reference (optional)">
          <input
            className="ims-input"
            placeholder="e.g., Shelf A, Damaged, etc."
            value={reference}
            onChange={e => setReference(e.target.value)}
          />
        </Field>
        <Field label="Authorized By (optional)">
          <input
            className="ims-input"
            placeholder="Your name"
            value={authorizedBy}
            onChange={e => setAuthorizedBy(e.target.value)}
          />
        </Field>
      </div>
    </ModalWrap>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADD ITEM MODAL (with bulk singles, duplicate detection, and bundle upsert)
// ═══════════════════════════════════════════════════════════════════════════════
const AddItemModal = ({ cat, onClose, onDone, showToast }) => {
  const isBooks = cat.name.toLowerCase() === "books";
  const accentColor = isBooks ? "#16a34a" : "#4f46e5";

  const [saving, setSaving] = useState(false);
  const [itemType, setItemType] = useState('single');
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [componentClass, setComponentClass] = useState(""); // for fetching singles (bundle components)

  // Single items – bulk rows
  const [singleRows, setSingleRows] = useState([{ itemName: '', singlePrice: '', subject: '' }]);
  const [existingSingleNames, setExistingSingleNames] = useState(new Set());

  // Bundle items
  const [bundleName, setBundleName] = useState("");
  const [originalBundleName, setOriginalBundleName] = useState("");
  const [availableSingles, setAvailableSingles] = useState([]);
  const [components, setComponents] = useState([]);
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState(0);
  const [manualPrice, setManualPrice] = useState("");
  const [existingBundles, setExistingBundles] = useState([]);
  const [selectedBundleId, setSelectedBundleId] = useState("");

  // Books subjects
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  // ───────────────────────────────────────────────────────────────────────────
  // Fetch subjects for Books
  useEffect(() => {
    if (isBooks && componentClass) {
      setSubjectsLoading(true);
      apiFetch("fetch-subjects-by-class", "GET", null, `class=${componentClass}`)
        .then(res => setSubjects(res.data || []))
        .catch(() => setSubjects([]))
        .finally(() => setSubjectsLoading(false));
    }
  }, [isBooks, componentClass]);

  // ───────────────────────────────────────────────────────────────────────────
  // Fetch singles when componentClass changes
  useEffect(() => {
    if (!componentClass) {
      setExistingSingleNames(new Set());
      setAvailableSingles([]);
      return;
    }
    apiFetch("fetch-items", "GET", null, `category_id=${cat.id}&type=single&class=${componentClass}`)
      .then(res => {
        const items = res.data || [];
        setAvailableSingles(items);
        setExistingSingleNames(new Set(items.map(i => i.item_name)));
      })
      .catch(() => {
        setAvailableSingles([]);
        setExistingSingleNames(new Set());
      });
  }, [componentClass, cat.id]);

  // ───────────────────────────────────────────────────────────────────────────
  // Fetch existing bundles when componentClass changes
  useEffect(() => {
    if (!componentClass) {
      setExistingBundles([]);
      return;
    }
    apiFetch("fetch-items", "GET", null, `category_id=${cat.id}&type=bundle&class=${componentClass}`)
      .then(res => setExistingBundles(res.data || []))
      .catch(() => setExistingBundles([]));
  }, [componentClass, cat.id]);

  // ───────────────────────────────────────────────────────────────────────────
  // Load selected bundle's data
  useEffect(() => {
    if (selectedBundleId && selectedBundleId !== "other") {
      const bundle = existingBundles.find(b => b.id === parseInt(selectedBundleId));
      if (bundle && bundle.components) {
        setComponents(bundle.components.map(c => ({
          item_id: c.component_item_id,
          quantity: c.quantity
        })));
        setBundleName(bundle.item_name);
        setOriginalBundleName(bundle.item_name);
        setSelectedClasses(bundle.classes || []);
        setDiscountValue(0);
        setManualPrice("");
      }
    } else if (selectedBundleId === "other") {
      setComponents([]);
      setBundleName("");
      setOriginalBundleName("");
      setSelectedClasses([]);
    }
  }, [selectedBundleId, existingBundles]);

  // ───────────────────────────────────────────────────────────────────────────
  // Compute bundle price
  const totalComponentPrice = components.reduce((sum, comp) => {
    const single = availableSingles.find(s => s.id === comp.item_id);
    return sum + (single?.single_price || 0) * comp.quantity;
  }, 0);

  let finalPrice = totalComponentPrice;
  if (discountType === 'percentage' && discountValue) {
    finalPrice = totalComponentPrice * (1 - discountValue / 100);
  } else if (discountType === 'fixed' && discountValue) {
    finalPrice = totalComponentPrice - discountValue;
  }
  if (finalPrice < 0) finalPrice = 0;

  // ───────────────────────────────────────────────────────────────────────────
  // Row management for singles
  const addRow = () => {
    setSingleRows([...singleRows, { itemName: '', singlePrice: '', subject: '' }]);
  };

  const updateRow = (index, field, value) => {
    const updated = [...singleRows];
    updated[index][field] = value;
    setSingleRows(updated);
  };

  const removeRow = (index) => {
    if (singleRows.length > 1) {
      setSingleRows(singleRows.filter((_, i) => i !== index));
    } else {
      setSingleRows([{ itemName: '', singlePrice: '', subject: '' }]);
    }
  };

  const isRowDuplicate = (row) => {
    const name = isBooks ? row.subject : row.itemName.trim();
    return name && existingSingleNames.has(name);
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Handle form submission
  const handleSubmit = async () => {
    if (selectedClasses.length === 0) {
      return showToast("Select at least one class for the item", "error");
    }

    if (itemType === 'single') {
      // Single validation
      for (let i = 0; i < singleRows.length; i++) {
        const row = singleRows[i];
        const rowItemName = isBooks ? row.subject : row.itemName.trim();
        if (isBooks && !row.subject) {
          return showToast(`Row ${i+1}: Subject is required`, "error");
        }
        if (!isBooks && !row.itemName.trim()) {
          return showToast(`Row ${i+1}: Item name is required`, "error");
        }
        if (!row.singlePrice || parseFloat(row.singlePrice) <= 0) {
          return showToast(`Row ${i+1}: Valid single price is required`, "error");
        }
        if (existingSingleNames.has(rowItemName)) {
          return showToast(`Row ${i+1}: "${rowItemName}" already exists in this class.`, "error");
        }
      }

      try {
        setSaving(true);
        const itemsPayload = singleRows.map(row => ({
          category_id: cat.id,
          classes: selectedClasses,
          item_name: isBooks ? row.subject : row.itemName.trim(),
          item_type: 'single',
          single_price: parseFloat(row.singlePrice),
          pair_price: null,
          created_by: "admin"
        }));

        await apiFetch("bulk-create-items", "POST", { items: itemsPayload });
        showToast(`${itemsPayload.length} item(s) added successfully!`, "success");
        setSingleRows([{ itemName: '', singlePrice: '', subject: '' }]);
        onDone();
      } catch (e) {
        showToast(e.message, "error");
      } finally {
        setSaving(false);
      }
    } else { // bundle
      if (!bundleName.trim()) return showToast("Bundle name is required", "error");
      if (components.length === 0) return showToast("Select at least one component", "error");

      const payload = {
        category_id: cat.id,
        classes: selectedClasses,
        item_name: bundleName.trim(),
        item_type: 'bundle',
        pair_price: manualPrice ? parseFloat(manualPrice) : finalPrice.toFixed(2),
        single_price: null,
        components: components.map(c => ({ item_id: c.item_id, quantity: c.quantity })),
        created_by: "admin"
      };

      try {
        setSaving(true);

        const isEditing = selectedBundleId && selectedBundleId !== "other" && bundleName.trim() === originalBundleName;

        if (isEditing) {
          await apiFetch("update-item", "POST", { ...payload, id: parseInt(selectedBundleId) });
          showToast("Bundle updated successfully!", "success");
          const updatedBundles = await apiFetch("fetch-items", "GET", null, `category_id=${cat.id}&type=bundle&class=${componentClass}`);
          setExistingBundles(updatedBundles.data || []);
        } else {
          await apiFetch("create-item", "POST", payload);
          showToast("Bundle created successfully!", "success");
          setBundleName("");
          setOriginalBundleName("");
          setComponents([]);
          setDiscountValue(0);
          setManualPrice("");
          setSelectedBundleId("");
          setSelectedClasses([]);
          const updatedBundles = await apiFetch("fetch-items", "GET", null, `category_id=${cat.id}&type=bundle&class=${componentClass}`);
          setExistingBundles(updatedBundles.data || []);
        }

        onDone();
      } catch (e) {
        showToast(e.message, "error");
      } finally {
        setSaving(false);
      }
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Render
  return (
    <ModalWrap size="ims-modal--lg" accentColor={accentColor}
      title={`Add New Item — ${cat.name}`}
      subtitle={itemType === 'single' ? "Add multiple items at once" : "Create or edit a bundle"}
      onClose={onClose}
      footer={
        <>
          <button className="ims-btn ims-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="ims-btn ims-btn--green" onClick={handleSubmit} disabled={saving}>
            {saving ? <><Spinner /> Saving…</> : "✓ Save"}
          </button>
        </>
      }
    >
      <div className="ims-form-section">
        <SecTitle>Item Details</SecTitle>

        {/* Multi-class picker */}
        <Field label="Applicable Classes" required>
          <ClassesPicker selected={selectedClasses} onChange={setSelectedClasses} />
        </Field>

        {/* For bundles: need a class to load components */}
        {itemType === 'bundle' && (
          <Field label="Component Class (to load singles)" required>
            <select className="ims-select" value={componentClass} onChange={e => setComponentClass(e.target.value)}>
              <option value="">Select Class</option>
              {(cat.classes || CLASSES).map(cl => <option key={cl}>{cl}</option>)}
            </select>
          </Field>
        )}

        {/* Item Type Toggle */}
        <Field label="Item Type">
          <div className="ims-chip-row">
            <button
              className={`ims-chip ${itemType === 'single' ? 'ims-chip--on' : ''}`}
              onClick={() => setItemType('single')}
            >📦 Single (Bulk Add)</button>
            <button
              className={`ims-chip ${itemType === 'bundle' ? 'ims-chip--on' : ''}`}
              onClick={() => setItemType('bundle')}
            >🔗 Bundle</button>
          </div>
        </Field>

        {itemType === 'single' && (
          <>
            <div className="ims-bulk-rows">
              {singleRows.map((row, idx) => {
                const duplicate = isRowDuplicate(row);
                return (
                  <div key={idx} className={`ims-bulk-row ${duplicate ? 'ims-duplicate-row' : ''}`}>
                    {isBooks ? (
                      <>
                        <Field label={`Row ${idx+1} Subject`} required>
                          <select
                            className="ims-select"
                            value={row.subject}
                            onChange={e => updateRow(idx, 'subject', e.target.value)}
                            disabled={!componentClass || subjectsLoading}
                          >
                            <option value="">Select Subject</option>
                            {subjects.map(s => <option key={s.id || s.name} value={s.name}>{s.name}</option>)}
                          </select>
                        </Field>
                        <Field label="Single Price (PKR)" required>
                          <input
                            className="ims-input"
                            type="number"
                            placeholder="0.00"
                            value={row.singlePrice}
                            onChange={e => updateRow(idx, 'singlePrice', e.target.value)}
                            disabled={duplicate}
                          />
                        </Field>
                      </>
                    ) : (
                      <>
                        <Field label={`Row ${idx+1} Item Name`} required>
                          <input
                            className="ims-input"
                            placeholder="e.g., Blue Shirt"
                            value={row.itemName}
                            onChange={e => updateRow(idx, 'itemName', e.target.value)}
                          />
                        </Field>
                        <Field label="Single Price (PKR)" required>
                          <input
                            className="ims-input"
                            type="number"
                            placeholder="0.00"
                            value={row.singlePrice}
                            onChange={e => updateRow(idx, 'singlePrice', e.target.value)}
                            disabled={duplicate}
                          />
                        </Field>
                      </>
                    )}
                    <button
                      className="ims-icon-btn ims-icon-btn--del"
                      onClick={() => removeRow(idx)}
                      title="Remove item"
                    >✕</button>
                    {duplicate && <span className="ims-duplicate-warning">Already exists</span>}
                  </div>
                );
              })}
            </div>
            <button className="ims-btn ims-btn--secondary ims-btn--sm" onClick={addRow}>
              + Add Another Item
            </button>
          </>
        )}

        {itemType === 'bundle' && (
          <>
            <Field label="Bundle Name" required>
              <input
                className="ims-input"
                placeholder="e.g., Summer Uniform Set"
                value={bundleName}
                onChange={e => setBundleName(e.target.value)}
              />
            </Field>

            <Field label="Copy from existing bundle (optional)">
              <select
                className="ims-select"
                value={selectedBundleId}
                onChange={e => setSelectedBundleId(e.target.value)}
                disabled={!componentClass}
              >
                <option value="">-- Select a bundle --</option>
                <option value="other">➕ Other (new bundle)</option>
                {existingBundles.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.item_name} (PKR {b.pair_price})
                  </option>
                ))}
              </select>
            </Field>

            <SecTitle>Components</SecTitle>
            {availableSingles.length === 0 ? (
              <div className="ims-placeholder">No single items found for this class. Create singles first.</div>
            ) : (
              <div className="ims-bundle-components">
                {availableSingles.map(single => {
                  const comp = components.find(c => c.item_id === single.id);
                  return (
                    <div key={single.id} className="ims-bundle-component-row">
                      <label className="ims-checkbox-label">
                        <input
                          type="checkbox"
                          checked={!!comp}
                          onChange={e => {
                            if (e.target.checked) {
                              setComponents([...components, { item_id: single.id, quantity: 1 }]);
                            } else {
                              setComponents(components.filter(c => c.item_id !== single.id));
                            }
                          }}
                        />
                        <span className="ims-component-name">{single.item_name}</span>
                        <span className="ims-component-price">PKR {single.single_price}</span>
                      </label>
                      {comp && (
                        <input
                          type="number"
                          min="1"
                          className="ims-component-qty"
                          value={comp.quantity}
                          onChange={e => {
                            const qty = parseInt(e.target.value) || 1;
                            setComponents(components.map(c =>
                              c.item_id === single.id ? { ...c, quantity: qty } : c
                            ));
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="ims-bundle-summary">
              <div className="ims-summary-row">
                <span>Total component price:</span>
                <span className="ims-summary-value">PKR {totalComponentPrice.toFixed(2)}</span>
              </div>
              <div className="ims-summary-row">
                <span>Discount:</span>
                <div className="ims-discount-inputs">
                  <select value={discountType} onChange={e => setDiscountType(e.target.value)}>
                    <option value="percentage">%</option>
                    <option value="fixed">Fixed (PKR)</option>
                  </select>
                  <input
                    type="number"
                    min="0"
                    value={discountValue}
                    onChange={e => setDiscountValue(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className="ims-summary-row ims-summary-total">
                <span>Final bundle price:</span>
                <span className="ims-summary-value">PKR {finalPrice.toFixed(2)}</span>
              </div>
              <Field label="Override final price (optional)">
                <input
                  className="ims-input"
                  type="number"
                  placeholder="Leave empty to use calculated price"
                  value={manualPrice}
                  onChange={e => setManualPrice(e.target.value)}
                />
              </Field>
            </div>
          </>
        )}
      </div>
    </ModalWrap>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT ITEM MODAL (for singles and bundles)
// ═══════════════════════════════════════════════════════════════════════════════
const EditItemModal = ({ item, onClose, onDone, showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itemData, setItemData] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [itemName, setItemName] = useState("");
  const [pairPrice, setPairPrice] = useState("");
  const [singlePrice, setSinglePrice] = useState("");

  // Bundle specific
  const [componentClass, setComponentClass] = useState("");
  const [availableSingles, setAvailableSingles] = useState([]);
  const [components, setComponents] = useState([]);
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState(0);
  const [manualPrice, setManualPrice] = useState("");

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const res = await apiFetch("fetch-item-details", "GET", null, `id=${item.id}`);
        setItemData(res);
        setSelectedClasses(res.classes || []);
        setItemName(res.item_name || "");
        setPairPrice(res.pair_price || "");
        setSinglePrice(res.single_price || "");
        if (res.item_type === 'bundle') {
          setComponents(res.components || []);
        }
      } catch (e) {
        showToast(e.message, "error");
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [item.id, showToast, onClose]);

  // Fetch singles when componentClass changes (for bundles)
  useEffect(() => {
    if (!itemData || itemData.item_type !== 'bundle') return;
    if (!componentClass) {
      setAvailableSingles([]);
      return;
    }
    apiFetch("fetch-items", "GET", null, `category_id=${itemData.category_id}&type=single&class=${componentClass}`)
      .then(res => setAvailableSingles(res.data || []))
      .catch(() => setAvailableSingles([]));
  }, [componentClass, itemData]);

  const totalComponentPrice = components.reduce((sum, comp) => {
    const single = availableSingles.find(s => s.id === (comp.component_item_id || comp.item_id));
    return sum + (single?.single_price || 0) * comp.quantity;
  }, 0);

  let finalPrice = totalComponentPrice;
  if (discountType === 'percentage' && discountValue) {
    finalPrice = totalComponentPrice * (1 - discountValue / 100);
  } else if (discountType === 'fixed' && discountValue) {
    finalPrice = totalComponentPrice - discountValue;
  }
  if (finalPrice < 0) finalPrice = 0;

  const handleSubmit = async () => {
    if (selectedClasses.length === 0) return showToast("Select at least one class", "error");
    if (!itemName.trim()) return showToast("Item name is required", "error");
    if (itemData?.item_type === 'bundle' && components.length === 0) {
      return showToast("Bundle must have at least one component", "error");
    }

    try {
      setSaving(true);
      const payload = {
        id: item.id,
        classes: selectedClasses,
        item_name: itemName,
        pair_price: itemData?.item_type === 'bundle' ? (manualPrice || finalPrice.toFixed(2)) : (pairPrice ? parseFloat(pairPrice) : null),
        single_price: itemData?.item_type === 'single' ? (singlePrice ? parseFloat(singlePrice) : null) : null,
        updated_by: "admin"
      };
      if (itemData?.item_type === 'bundle') {
        payload.components = components.map(c => ({
          item_id: c.component_item_id || c.item_id,
          quantity: c.quantity
        }));
      }
      await apiFetch("update-item", "POST", payload);
      onDone();
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ModalWrap title="Loading..." onClose={onClose}>
        <div className="ims-loading-row"><Spinner dark /> Loading item details…</div>
      </ModalWrap>
    );
  }

  const isBundle = itemData?.item_type === 'bundle';

  return (
    <ModalWrap size="ims-modal--lg"
      title={`Edit ${isBundle ? 'Bundle' : 'Item'}: ${itemData?.item_name}`}
      onClose={onClose}
      footer={
        <>
          <button className="ims-btn ims-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="ims-btn ims-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <><Spinner /> Saving…</> : "Update Item"}
          </button>
        </>
      }
    >
      <div className="ims-form-section">
        <SecTitle>Basic Information</SecTitle>

        {/* Multi-class picker */}
        <Field label="Applicable Classes" required>
          <ClassesPicker selected={selectedClasses} onChange={setSelectedClasses} />
        </Field>

        {isBundle && (
          <Field label="Component Class (to load singles)" required>
            <select className="ims-select" value={componentClass} onChange={e => setComponentClass(e.target.value)}>
              <option value="">Select Class</option>
              {(itemData?.classes || CLASSES).map(cl => <option key={cl}>{cl}</option>)}
            </select>
          </Field>
        )}

        <Field label="Item Name" required>
          <input className="ims-input" value={itemName} onChange={e => setItemName(e.target.value)} />
        </Field>

        {!isBundle && (
          <div className="ims-row-2">
            <Field label="Pair Price (PKR)" optional>
              <input className="ims-input" type="number" value={pairPrice} onChange={e => setPairPrice(e.target.value)} />
            </Field>
            <Field label="Single Price (PKR)" optional>
              <input className="ims-input" type="number" value={singlePrice} onChange={e => setSinglePrice(e.target.value)} />
            </Field>
          </div>
        )}
      </div>

      {isBundle && (
        <div className="ims-form-section">
          <SecTitle>Bundle Components</SecTitle>
          {availableSingles.length === 0 ? (
            <div className="ims-placeholder">No singles available for this class.</div>
          ) : (
            <div className="ims-bundle-components">
              {availableSingles.map(single => {
                const comp = components.find(c => (c.component_item_id || c.item_id) === single.id);
                return (
                  <div key={single.id} className="ims-bundle-component-row">
                    <label className="ims-checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!comp}
                        onChange={e => {
                          if (e.target.checked) {
                            setComponents([...components, { item_id: single.id, quantity: 1 }]);
                          } else {
                            setComponents(components.filter(c => (c.component_item_id || c.item_id) !== single.id));
                          }
                        }}
                      />
                      <span className="ims-component-name">{single.item_name}</span>
                      <span className="ims-component-price">PKR {single.single_price}</span>
                    </label>
                    {comp && (
                      <input
                        type="number"
                        min="1"
                        className="ims-component-qty"
                        value={comp.quantity}
                        onChange={e => {
                          const qty = parseInt(e.target.value) || 1;
                          setComponents(components.map(c =>
                            (c.component_item_id || c.item_id) === single.id ? { ...c, quantity: qty } : c
                          ));
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="ims-bundle-summary">
            <div className="ims-summary-row">
              <span>Total component price:</span>
              <span className="ims-summary-value">PKR {totalComponentPrice.toFixed(2)}</span>
            </div>
            <div className="ims-summary-row">
              <span>Discount:</span>
              <div className="ims-discount-inputs">
                <select value={discountType} onChange={e => setDiscountType(e.target.value)}>
                  <option value="percentage">%</option>
                  <option value="fixed">Fixed (PKR)</option>
                </select>
                <input
                  type="number"
                  min="0"
                  value={discountValue}
                  onChange={e => setDiscountValue(parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="ims-summary-row ims-summary-total">
              <span>Final bundle price:</span>
              <span className="ims-summary-value">PKR {finalPrice.toFixed(2)}</span>
            </div>
            <Field label="Override final price (optional)">
              <input
                className="ims-input"
                type="number"
                placeholder="Leave empty to use calculated price"
                value={manualPrice}
                onChange={e => setManualPrice(e.target.value)}
              />
            </Field>
          </div>
        </div>
      )}
    </ModalWrap>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// BULK STOCK INTAKE MODAL – with smart summary (complete sets possible)
// ═══════════════════════════════════════════════════════════════════════════════
const BulkStockIntakeModal = ({ cat, onClose, onDone, showToast }) => {
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState("");
  const [items, setItems] = useState([]);           // all single items for the class
  const [selectedItems, setSelectedItems] = useState({}); // { [itemId]: boolean }
  const [extraSingles, setExtraSingles] = useState({});   // { [itemId]: number }
  const [completeSets, setCompleteSets] = useState("");
  const [reference, setReference] = useState("");
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch items when class changes
  useEffect(() => {
    if (!selectedClass) {
      setItems([]);
      setSelectedItems({});
      setExtraSingles({});
      return;
    }
    setLoading(true);
    apiFetch("fetch-items", "GET", null, `category_id=${cat.id}&type=single&class=${selectedClass}`)
      .then(res => {
        const fetched = res.data || [];
        setItems(fetched);
        const initSelected = {};
        fetched.forEach(item => { initSelected[item.id] = true; });
        setSelectedItems(initSelected);
        const initExtra = {};
        fetched.forEach(item => { initExtra[item.id] = 0; });
        setExtraSingles(initExtra);
      })
      .catch(e => showToast(e.message, "error"))
      .finally(() => setLoading(false));
  }, [selectedClass, cat.id]);

  // Toggle selection of an item
  const toggleItem = (itemId) => {
    setSelectedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  // Update extra singles for an item
  const updateExtra = (itemId, value) => {
    const num = parseInt(value) || 0;
    setExtraSingles(prev => ({ ...prev, [itemId]: num < 0 ? 0 : num }));
  };

  // Calculate new stock for an item after intake
  const calculateNewStock = (item) => {
    const sets = parseInt(completeSets) || 0;
    const extra = extraSingles[item.id] || 0;
    const current = item.current_single_qty || 0;
    return current + sets + extra;
  };

  // Compute summary values for selected items
  const selectedItemsList = items.filter(item => selectedItems[item.id]);
  const newQtys = selectedItemsList.map(item => calculateNewStock(item));
  const minNewQty = newQtys.length ? Math.min(...newQtys) : 0;
  const totalExtra = newQtys.reduce((sum, qty) => sum + (qty - minNewQty), 0);
  const totalItems = newQtys.reduce((sum, qty) => sum + qty, 0);

  const handleSubmit = async () => {
    if (!selectedClass) return showToast("Select a class", "error");
    const sets = parseInt(completeSets) || 0;
    if (sets <= 0 && Object.values(extraSingles).every(v => v === 0)) {
      return showToast("Enter at least one complete set or some extra singles", "error");
    }

    const adjustments = [];
    items.forEach(item => {
      if (selectedItems[item.id]) {
        const add = (parseInt(completeSets) || 0) + (extraSingles[item.id] || 0);
        if (add > 0) {
          adjustments.push({
            item_id: item.id,
            quantity: add
          });
        }
      }
    });

    if (adjustments.length === 0) {
      return showToast("No items selected or quantities to add", "error");
    }

    try {
      setSaving(true);
      await apiFetch("bulk-adjust-stock", "POST", {
        adjustments,
        entry_type: "IN",
        reference_details: reference || `Bulk intake for ${cat.name} - ${selectedClass}`,
        authorized_by: authorizedBy || "admin",
        created_by: "admin"
      });
      showToast("Stock updated successfully!", "success");
      onDone();
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrap size="ims-modal--lg" accentColor="#4f46e5"
      title={`Bulk Stock Intake — ${cat.name}`}
      subtitle="Add complete sets and extra singles"
      onClose={onClose}
      footer={
        <>
          <button className="ims-btn ims-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="ims-btn ims-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <><Spinner /> Updating…</> : "✓ Update Stock"}
          </button>
        </>
      }
    >
      <div className="ims-form-section">
        <SecTitle>Select Class</SecTitle>
        <Field label="Class" required>
          <select className="ims-select" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
            <option value="">All Classes</option>
            {(cat.classes || CLASSES).map(cl => <option key={cl}>{cl}</option>)}
          </select>
        </Field>
      </div>

      {loading && (
        <div className="ims-loading-row"><Spinner dark /> Loading items…</div>
      )}

      {!loading && items.length > 0 && (
        <>
          <div className="ims-form-section">
            <SecTitle>Complete Sets</SecTitle>
            <Field label="Number of complete sets (adds to all selected items)">
              <input
                className="ims-input"
                type="number"
                min="0"
                placeholder="e.g., 10"
                value={completeSets}
                onChange={e => setCompleteSets(e.target.value)}
              />
            </Field>
          </div>

          <div className="ims-form-section">
            <SecTitle>Items</SecTitle>
            <div className="ims-bulk-items-grid">
              {items.map(item => {
                const isSelected = selectedItems[item.id];
                const newQty = calculateNewStock(item);
                return (
                  <div key={item.id} className={`ims-bulk-item-card ${isSelected ? 'selected' : ''}`}>
                    <div className="ims-bulk-item-header">
                      <label className="ims-checkbox-label">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleItem(item.id)}
                        />
                        <span className="ims-item-name">{item.item_name}</span>
                      </label>
                    </div>
                    <div className="ims-bulk-item-details">
                      <div className="ims-bulk-item-row">
                        <span className="ims-bulk-item-label">Current stock:</span>
                        <span className="ims-bulk-item-value">{item.current_single_qty || 0}</span>
                      </div>
                      <div className="ims-bulk-item-row">
                        <span className="ims-bulk-item-label">Extra singles:</span>
                        <input
                          type="number"
                          min="0"
                          className="ims-input ims-input--sm"
                          value={extraSingles[item.id] || 0}
                          onChange={e => updateExtra(item.id, e.target.value)}
                          disabled={!isSelected}
                        />
                      </div>
                      <div className="ims-bulk-item-row ims-bulk-item-total">
                        <span className="ims-bulk-item-label">New stock:</span>
                        <span className="ims-bulk-item-value">{newQty}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedItemsList.length > 0 && (
            <div className="ims-form-section">
              <SecTitle>Intake Summary</SecTitle>
              <div className="ims-summary-card">
                <div className="ims-summary-row">
                  <span>Complete sets being added:</span>
                  <strong>{parseInt(completeSets) || 0}</strong>
                </div>
                <div className="ims-summary-row">
                  <span>Complete sets possible after intake:</span>
                  <strong>{minNewQty}</strong>
                </div>
                <div className="ims-summary-row">
                  <span>Extra singles after intake:</span>
                  <strong>{totalExtra}</strong>
                </div>
                <div className="ims-summary-row ims-summary-total">
                  <span>Total items after intake:</span>
                  <strong>{totalItems}</strong>
                </div>
              </div>
            </div>
          )}

          <div className="ims-form-section">
            <SecTitle>Optional Details</SecTitle>
            <Field label="Reference / Location" optional>
              <input
                className="ims-input"
                placeholder="e.g., Shelf A, Received from Supplier"
                value={reference}
                onChange={e => setReference(e.target.value)}
              />
            </Field>
            <Field label="Authorized By" optional>
              <input
                className="ims-input"
                placeholder="e.g., Storekeeper name"
                value={authorizedBy}
                onChange={e => setAuthorizedBy(e.target.value)}
              />
            </Field>
          </div>
        </>
      )}

      {!loading && items.length === 0 && selectedClass && (
        <div className="ims-placeholder">No single items found for this class. Create some first.</div>
      )}
    </ModalWrap>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY ITEMS MODAL (Workspace)
// ═══════════════════════════════════════════════════════════════════════════════
const CategoryItemsModal = ({ cat, onClose, onDone, showToast, onEditItem }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'single', 'bundle'

  useEffect(() => {
    loadItems();
  }, [cat.id]);

  const loadItems = async () => {
    try {
      const res = await apiFetch("fetch-items", "GET", null, `category_id=${cat.id}`);
      setItems(res.data || []);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete ${item.item_name}?`)) return;
    try {
      await apiFetch("delete-item", "POST", { id: item.id, deleted_by: "admin" });
      showToast("Item deleted");
      loadItems();
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  const filteredItems = items.filter(item => filter === 'all' || item.item_type === filter);

  return (
    <ModalWrap size="ims-modal--lg" title={`Items in ${cat.name}`} onClose={onClose}
      footer={<button className="ims-btn ims-btn--ghost" onClick={onClose}>Close</button>}
    >
      <div className="ims-items-header">
        <div className="ims-filter-tabs">
          <button className={`ims-filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
          <button className={`ims-filter-tab ${filter === 'single' ? 'active' : ''}`} onClick={() => setFilter('single')}>Singles</button>
          <button className={`ims-filter-tab ${filter === 'bundle' ? 'active' : ''}`} onClick={() => setFilter('bundle')}>Bundles</button>
        </div>
      </div>

      {loading ? (
        <div className="ims-loading-row"><Spinner dark /> Loading items…</div>
      ) : filteredItems.length === 0 ? (
        <div className="ims-placeholder">No items in this category.</div>
      ) : (
        <div className="ims-items-list">
          {filteredItems.map(item => (
            <div key={item.id} className={`ims-item-card ${item.item_type}`}>
              <div className="ims-item-icon">
                {item.item_type === 'bundle' ? '🔗' : '📦'}
              </div>
              <div className="ims-item-info">
                <div className="ims-item-name">{item.item_name}</div>
                <div className="ims-item-meta">
                  Classes: {(item.classes || []).join(', ') || '—'} | 
                  {item.item_type === 'single' ? (
                    <> Pair: PKR {item.pair_price || '—'} / Single: PKR {item.single_price || '—'}</>
                  ) : (
                    <> Bundle Price: PKR {item.pair_price || '—'}</>
                  )}
                </div>
                {item.item_type === 'bundle' && item.components && (
                  <div className="ims-bundle-components-list">
                    <small>Components:</small>
                    {item.components.map(comp => (
                      <span key={comp.component_item_id || comp.item_id} className="ims-component-tag">
                        {comp.item_name} x{comp.quantity}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="ims-item-actions">
                <button className="ims-icon-btn" onClick={() => onEditItem(item)} title="Edit">✏️</button>
                <button className="ims-icon-btn ims-icon-btn--del" onClick={() => handleDelete(item)} title="Delete">🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </ModalWrap>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY ACTION MODAL (UPDATED – now with Warehouse)
// ═══════════════════════════════════════════════════════════════════════════════
const CategoryActionModal = ({ cat, onClose, onOpenAddItem, onOpenIntake, onOpenWorkspace, onOpenWarehouse }) => {
  const isFixed = !!cat.is_fixed;
  const accentColor = isFixed ? "#16a34a" : "#4f46e5";

  return (
    <ModalWrap size="ims-modal--md" accentColor={accentColor}
      title={`Manage — ${cat.name}`}
      subtitle="Choose an action"
      onClose={onClose}
      footer={
        <button className="ims-btn ims-btn--ghost" onClick={onClose}>Cancel</button>
      }
    >
      <div className="ims-action-grid">
        <button className="ims-action-card" onClick={() => { onClose(); onOpenAddItem(); }}>
          <span className="ims-action-icon">➕💰</span>
          <span className="ims-action-title">Add New Catalog Item / Pricing</span>
          <span className="ims-action-desc">Create a new item and set its prices</span>
        </button>
        <button className="ims-action-card" onClick={() => { onClose(); onOpenIntake(); }}>
          <span className="ims-action-icon">📦➕</span>
          <span className="ims-action-title">Bulk Stock Intake</span>
          <span className="ims-action-desc">Add complete sets and extra singles for multiple items</span>
        </button>
        <button className="ims-action-card" onClick={() => { onClose(); onOpenWorkspace(); }}>
          <span className="ims-action-icon">📋🔍</span>
          <span className="ims-action-title">View & Edit Items</span>
          <span className="ims-action-desc">List all items, edit bundles, delete</span>
        </button>
        <button className="ims-action-card" onClick={() => { onClose(); onOpenWarehouse(); }}>
          <span className="ims-action-icon">🏭📦</span>
          <span className="ims-action-title">Warehouse</span>
          <span className="ims-action-desc">View & adjust stock for all items</span>
        </button>
      </div>
    </ModalWrap>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY CARD
// ═══════════════════════════════════════════════════════════════════════════════
const CategoryCard = ({ cat, color, onEdit, onDelete, onManage }) => {
  const classes = cat.classes || [];
  const isFixed = !!cat.is_fixed;

  return (
    <div className="ims-card">
      <div className="ims-card-bar" style={{ background: color }} />

      <div className="ims-card-header">
        <div className="ims-card-thumb">
          {cat.icon_name ? (
            <span style={{ fontSize: "2rem" }}>
              {cat.icon_name === "book" ? "📚" :
               cat.icon_name === "shirt" ? "👕" :
               cat.icon_name === "pen" ? "🖊️" :
               cat.icon_name === "bag" ? "🎒" :
               cat.icon_name === "shoe" ? "👟" : "📦"}
            </span>
          ) : (
            <span style={{ fontSize: "2rem" }}>📦</span>
          )}
        </div>

        <div className="ims-card-info">
          <span className="ims-card-name">{cat.name}</span>
          {isFixed
            ? <span className="ims-badge-default">📌 Fixed</span>
            : <span className="ims-card-meta">{fmtDate(cat.created_at)}</span>}
        </div>

        <div className="ims-card-actions">
          <button className="ims-icon-btn" onClick={onEdit} disabled={isFixed} title="Edit">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button className="ims-icon-btn ims-icon-btn--del" onClick={onDelete} disabled={isFixed} title="Delete">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="ims-card-stats ims-card-stats--grid4">
        <div className="ims-sc">
          <span className="ims-sc-val">{cat.total_pairs ?? 0}</span>
          <span className="ims-sc-lbl">Pairs</span>
        </div>
        <div className="ims-sc">
          <span className="ims-sc-val">{cat.total_singles ?? 0}</span>
          <span className="ims-sc-lbl">Singles</span>
        </div>
        <div className="ims-sc">
          <span className="ims-sc-val">{cat.total_bundles ?? 0}</span>
          <span className="ims-sc-lbl">Complete Sets</span>
        </div>
        <div className="ims-sc">
          <span className="ims-sc-val">{cat.items_count ?? 0}</span>
          <span className="ims-sc-lbl">Items</span>
        </div>
      </div>

      <div className="ims-class-tags">
        {classes.slice(0, 7).map(cl => <span key={cl} className="ims-class-tag">{cl}</span>)}
        {classes.length > 7 && <span className="ims-class-tag ims-class-tag--more">+{classes.length - 7}</span>}
      </div>

      <div className="ims-card-footer">
        <button className="ims-btn ims-btn--primary ims-btn--sm" onClick={onManage}>
          🛠️ Manage
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WAREHOUSE SCREEN (ENHANCED with stat cards, chart, and CRUD modals)
// ═══════════════════════════════════════════════════════════════════════════════
const WarehouseScreen = ({ cat, onBack, showToast, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState("");
  const [items, setItems] = useState([]);
  const [adjustingId, setAdjustingId] = useState(null);

  // State for modals inside warehouse
  const [modal, setModal] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [adjustModal, setAdjustModal] = useState({ open: false, item: null, type: null });

  // Fetch items when class changes
  useEffect(() => {
    setLoading(true);
    apiFetch("fetch-items", "GET", null, `category_id=${cat.id}${selectedClass ? `&class=${selectedClass}` : ''}`)
      .then(res => setItems(res.data || []))
      .catch(e => showToast(e.message, "error"))
      .finally(() => setLoading(false));
  }, [selectedClass, cat.id]);

  // Compute stats for this warehouse view
  const totalItems = items.reduce((sum, item) => {
    const qty = item.item_type === 'bundle'
      ? (item.current_bundle_qty || 0)
      : (item.current_single_qty || 0);
    return sum + qty;
  }, 0);

  const totalValue = items.reduce((sum, item) => {
    const qty = item.item_type === 'bundle'
      ? (item.current_bundle_qty || 0)
      : (item.current_single_qty || 0);
    const price = item.item_type === 'bundle'
      ? (item.pair_price || 0)
      : (item.single_price || 0);
    return sum + (qty * price);
  }, 0);

  const lowStockCount = items.filter(item => {
    const qty = item.item_type === 'bundle'
      ? (item.current_bundle_qty || 0)
      : (item.current_single_qty || 0);
    return qty > 0 && qty < LOW_STOCK_THRESHOLD;
  }).length;

  const totalBundleQty = items.reduce((sum, item) => {
    if (item.item_type === 'bundle') {
      return sum + (item.current_bundle_qty || 0);
    }
    return sum;
  }, 0);

  // Chart data
  const chartData = {
    labels: items.slice(0, 10).map(item => item.item_name.substring(0, 15) + (item.item_name.length > 15 ? '…' : '')),
    datasets: [
      {
        label: 'Stock Quantity',
        data: items.slice(0, 10).map(item =>
          item.item_type === 'bundle'
            ? (item.current_bundle_qty || 0)
            : (item.current_single_qty || 0)
        ),
        backgroundColor: items.map(item => item.item_type === 'bundle' ? '#8b5cf6' : '#4f46e5'),
        borderRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: { backgroundColor: '#0f172a' },
    },
    scales: {
      y: { beginAtZero: true, grid: { color: '#e2e8f0' } },
      x: { grid: { display: false } },
    },
  };

  // CRUD modal handlers
  const openAddItem = () => setModal("addItem");
  const openEditItem = (item) => {
    setSelectedItem(item);
    setModal("editItem");
  };
  const openDeleteItem = (item) => {
    setItemToDelete(item);
    setModal("deleteItem");
  };
  const openAdjustModal = (item, type) => {
    setAdjustModal({ open: true, item, type });
  };
  const closeAdjustModal = () => {
    setAdjustModal({ open: false, item: null, type: null });
  };
  const closeModal = () => {
    setModal(null);
    setSelectedItem(null);
    setItemToDelete(null);
  };

  const afterItemAdd = async () => {
    await refreshItems();
    showToast("Item added successfully!", "success");
    closeModal();
  };

  const afterItemUpdate = async () => {
    await refreshItems();
    showToast("Item updated successfully!", "success");
    closeModal();
  };

  const afterItemDelete = async () => {
    await refreshItems();
    closeModal();
  };

  const afterBulkIntake = async () => {
    await refreshItems();
    showToast("Bulk intake completed!", "success");
    closeModal();
  };

  const afterAdjust = async () => {
    await refreshItems();
    closeAdjustModal();
  };

  const refreshItems = async () => {
    const res = await apiFetch("fetch-items", "GET", null, `category_id=${cat.id}${selectedClass ? `&class=${selectedClass}` : ''}`);
    setItems(res.data || []);
    if (onDone) onDone();
  };

  return (
    <div className="ims-warehouse-screen">
      <div className="ims-warehouse-header">
        <button className="ims-btn ims-btn--ghost" onClick={onBack}>
          ← Back to Dashboard
        </button>
        <h2>Warehouse — {cat.name}</h2>
      </div>

      {/* Stat Cards */}
      <div className="ims-stats-grid">
        <div className="ims-stat-card">
          <div className="ims-stat-icon" style={{ background: "#eef2ff" }}>📦</div>
          <div>
            <div className="ims-stat-val">{totalItems}</div>
            <div className="ims-stat-name">Total Items in Stock</div>
          </div>
        </div>
        <div className="ims-stat-card">
          <div className="ims-stat-icon" style={{ background: "#f0fdf4" }}>💰</div>
          <div>
            <div className="ims-stat-val">PKR {totalValue.toLocaleString()}</div>
            <div className="ims-stat-name">Total Value</div>
          </div>
        </div>
        <div className="ims-stat-card">
          <div className="ims-stat-icon" style={{ background: "#fff7ed" }}>⚠️</div>
          <div>
            <div className="ims-stat-val">{lowStockCount}</div>
            <div className="ims-stat-name">Low Stock Alerts</div>
          </div>
        </div>
        <div className="ims-stat-card">
          <div className="ims-stat-icon" style={{ background: "#fdf4ff" }}>🔗</div>
          <div>
            <div className="ims-stat-val">{totalBundleQty}</div>
            <div className="ims-stat-name">Total Complete Sets</div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="ims-warehouse-actions-bar" style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
        <button className="ims-btn ims-btn--primary" onClick={openAddItem}>
          + Add New Item
        </button>
        <button className="ims-btn ims-btn--green" onClick={() => setModal("bulkIntake")}>
          📦 Bulk Intake
        </button>
      </div>

      {/* Filter by class */}
      <div className="ims-form-section" style={{ marginBottom: '2rem' }}>
        <SecTitle>Filter by Class</SecTitle>
        <Field label="Class">
          <select className="ims-select" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
            <option value="">All Classes</option>
            {(cat.classes || CLASSES).map(cl => <option key={cl}>{cl}</option>)}
          </select>
        </Field>
      </div>

      {/* Chart */}
      {items.length > 0 && (
        <div className="ims-chart-container" style={{ height: '240px', marginBottom: '2rem' }}>
          <Bar data={chartData} options={chartOptions} />
        </div>
      )}

      {loading && (
        <div className="ims-loading-row"><Spinner dark /> Loading items…</div>
      )}

      {!loading && items.length === 0 && selectedClass && (
        <div className="ims-placeholder">No items found for this class.</div>
      )}

      {/* Items grid */}
      {!loading && items.length > 0 && (
        <div className="ims-items-grid">
          {items.map(item => {
            const currentStock = item.item_type === 'bundle'
              ? (item.current_bundle_qty ?? 0)
              : (item.current_single_qty ?? 0);

            return (
              <div key={item.id} className={`ims-warehouse-card ${item.item_type}`}>
                <div className="ims-warehouse-icon">
                  {item.item_type === 'bundle' ? '🔗' : '📦'}
                </div>
                <div className="ims-warehouse-info">
                  <div className="ims-warehouse-name">{item.item_name}</div>
                  <div className="ims-warehouse-meta">
                    {item.item_type === 'single' ? (
                      <>Price: PKR {item.single_price || '—'}</>
                    ) : (
                      <>Bundle Price: PKR {item.pair_price || '—'}</>
                    )}
                  </div>
                  {item.item_type === 'bundle' && item.components && item.components.length > 0 && (
                    <div className="ims-bundle-components-mini">
                      {item.components.map(comp => (
                        <span key={comp.component_item_id} className="ims-component-mini-tag">
                          {comp.item_name} ×{comp.quantity}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="ims-warehouse-stock">
                    <span className="ims-stock-label">In stock:</span>
                    <span className="ims-stock-value">{currentStock}</span>
                  </div>
                </div>
                <div className="ims-warehouse-actions">
                  <button
                    className="ims-btn ims-btn--success ims-btn--xs"
                    onClick={() => openAdjustModal(item, "IN")}
                    disabled={adjustingId === item.id}
                  >
                    {adjustingId === item.id ? <Spinner /> : '➕ Add'}
                  </button>
                  <button
                    className="ims-btn ims-btn--warning ims-btn--xs"
                    onClick={() => openAdjustModal(item, "OUT")}
                    disabled={adjustingId === item.id || currentStock <= 0}
                  >
                    {adjustingId === item.id ? <Spinner /> : '➖ Remove'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {modal === "addItem" && (
        <AddItemModal
          cat={cat}
          onClose={closeModal}
          onDone={afterItemAdd}
          showToast={showToast}
        />
      )}
      {modal === "editItem" && selectedItem && (
        <EditItemModal
          item={selectedItem}
          onClose={closeModal}
          onDone={afterItemUpdate}
          showToast={showToast}
        />
      )}
      {modal === "deleteItem" && itemToDelete && (
        <DeleteItemModal
          item={itemToDelete}
          onClose={closeModal}
          onDone={afterItemDelete}
          showToast={showToast}
        />
      )}
      {modal === "bulkIntake" && (
        <BulkStockIntakeModal
          cat={cat}
          onClose={closeModal}
          onDone={afterBulkIntake}
          showToast={showToast}
        />
      )}
      {adjustModal.open && (
        <AdjustStockModal
          item={adjustModal.item}
          cat={cat}
          type={adjustModal.type}
          onClose={closeAdjustModal}
          onDone={afterAdjust}
          showToast={showToast}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
const InventoryDashboard = () => {
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' or 'warehouse'
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState(null);

  // Recent entries
  const [activities, setActivities] = useState([]);
  const [activitiesTotal, setActivitiesTotal] = useState(0);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesPage, setActivitiesPage] = useState(0);
  const ACTIVITIES_LIMIT = 15;

  const loadCategories = useCallback(async () => {
    const res = await apiFetch("fetch-categories");
    setCategories(res.data || []);
  }, []);

  const loadAllItems = useCallback(async () => {
    try {
      const res = await apiFetch("fetch-items");
      setAllItems(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const loadStats = useCallback(async () => {
    try { const res = await apiFetch("fetch-dashboard-stats"); setStats(res.stats); } catch {}
  }, []);

  const loadActivities = useCallback(async (offset = 0) => {
    setActivitiesLoading(true);
    try {
      const res = await apiFetch("fetch-today-entries", "GET", null, `limit=${ACTIVITIES_LIMIT}&offset=${offset}`);
      if (offset === 0) setActivities(res.data || []);
      else setActivities(prev => [...prev, ...(res.data || [])]);
      setActivitiesTotal(res.total || 0);
    } catch (e) {
      // ignore
    } finally {
      setActivitiesLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadCategories(), loadAllItems(), loadStats(), loadActivities()]);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const openModal = (type, cat = null, item = null) => { 
    setModal(type); 
    setSelectedCat(cat);
    setEditItem(item);
  };
  const closeModal = () => { setModal(null); setSelectedCat(null); setEditItem(null); };
  const refresh = async () => { await Promise.all([loadCategories(), loadAllItems(), loadStats(), loadActivities()]); };

  const afterCreate = async () => { await refresh(); showToast("Category created!"); closeModal(); };
  const afterUpdate = async () => { await refresh(); showToast("Category updated!"); closeModal(); };
  const afterDelete = async () => { await refresh(); showToast("Category deleted."); closeModal(); };
  const afterItemAdd = async () => { await refresh(); showToast("Item added!"); closeModal(); };
  const afterItemUpdate = async () => { await refresh(); showToast("Item updated!"); closeModal(); };
  const afterMovement = async () => { await refresh(); showToast("Stock movement recorded!"); closeModal(); };

  // Compute global metrics
  const totalInventoryValue = allItems.reduce((sum, item) => {
    const pairValue = (item.current_pair_qty || 0) * (item.pair_price || 0);
    const singleValue = (item.current_single_qty || 0) * (item.single_price || 0);
    return sum + pairValue + singleValue;
  }, 0);

  const lowStockCount = allItems.filter(item => {
    const total = (item.current_pair_qty || 0) + (item.current_single_qty || 0);
    return total > 0 && total < LOW_STOCK_THRESHOLD;
  }).length;

  const todayIn = stats?.today_in || 0;
  const todayOut = stats?.today_out || 0;

  const statCards = [
    { label: "Total Value (PKR)", val: totalInventoryValue.toLocaleString(), icon: "💰", bg: "#eef2ff" },
    { label: "Active Categories",  val: stats?.total_categories ?? "—",       icon: "🗂️", bg: "#f0fdf4" },
    { label: "Low Stock Alerts",   val: lowStockCount,                        icon: "⚠️", bg: "#fff7ed" },
    { label: "Today's Movement",   val: `📥${todayIn} / 📤${todayOut}`,       icon: "📊", bg: "#fdf4ff" },
  ];

  // If in warehouse mode, show warehouse screen
  if (viewMode === 'warehouse' && selectedCat) {
    return (
      <div style={{ display: "flex" }}>
        <Sidebar />
        <div className="ims-main">
          <WarehouseScreen
            cat={selectedCat}
            onBack={() => {
              setViewMode('dashboard');
              setSelectedCat(null);
            }}
            showToast={showToast}
            onDone={refresh}
          />
          {toast && <Toast msg={toast.msg} type={toast.type} />}
        </div>
      </div>
    );
  }

  // Default dashboard view
  return (
    <div style={{ display: "flex" }}>
      <Sidebar />

      <div className="ims-main">
        {/* Page header */}
        <div className="ims-page-header">
          <div>
            <h1 className="ims-page-title">📦 Inventory Command Center</h1>
            <p className="ims-page-sub">
              {new Date().toLocaleDateString("en-PK", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
          <button className="ims-btn ims-btn--primary" onClick={() => openModal("createCat")}>
            + Create Category
          </button>
        </div>

        {/* Global Metrics */}
        <div className="ims-stats-grid">
          {statCards.map((s, i) => (
            <div key={i} className="ims-stat-card">
              <div className="ims-stat-icon" style={{ background: s.bg }}>{s.icon}</div>
              <div>
                <div className="ims-stat-val">{loading ? "…" : s.val}</div>
                <div className="ims-stat-name">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Section heading */}
        <div className="ims-section-header">
          <div className="ims-section-heading">
            Categories <span className="ims-section-count">{categories.length}</span>
          </div>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="ims-loading-row"><Spinner dark /> Loading categories…</div>
        ) : categories.length === 0 ? (
          <div className="ims-empty">
            <div style={{ fontSize: 48 }}>📭</div>
            <p>No categories yet. Click <strong>+ Create Category</strong> to get started.</p>
          </div>
        ) : (
          <div className="ims-cards-grid">
            {categories.map((cat, idx) => (
              <CategoryCard
                key={cat.id}
                cat={cat}
                color={cat.is_fixed ? "#16a34a" : CAT_COLORS[idx % CAT_COLORS.length]}
                onEdit={()   => openModal("editCat",   cat)}
                onDelete={() => openModal("deleteCat", cat)}
                onManage={() => openModal("actionMenu", cat)}
              />
            ))}
          </div>
        )}

        {/* Recent Activities */}
        <div className="ims-activities-section">
          <div className="ims-activities-header">
            <div className="ims-activities-title">
              Recent Activities
              <span className="ims-activities-count">{activitiesTotal}</span>
            </div>
            <button
              className="ims-btn ims-btn--ghost ims-btn--xs"
              onClick={() => { setActivitiesPage(0); loadActivities(0); }}
              disabled={activitiesLoading}
            >
              ↻ Refresh
            </button>
          </div>

          {activitiesLoading && activities.length === 0 ? (
            <div className="ims-loading-row"><Spinner dark /> Loading activities…</div>
          ) : activities.length === 0 ? (
            <div className="ims-empty">
              <span style={{ fontSize: 32 }}>📭</span>
              <p>No recent activities</p>
            </div>
          ) : (
            <>
              <table className="ims-activities-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Item</th>
                    <th>Pairs</th>
                    <th>Singles</th>
                    <th>Reference</th>
                    <th>Authorized</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map(act => (
                    <tr key={act.id}>
                      <td>
                        <span className={act.entry_type === "IN" ? "ims-badge-in" : "ims-badge-out"}>
                          {act.entry_type}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>{act.category_name}</td>
                      <td>{act.item_name}</td>
                      <td className="ims-mono">{act.qty_pairs_changed}</td>
                      <td className="ims-mono">{act.qty_singles_changed}</td>
                      <td className="ims-muted">{act.reference_details || "—"}</td>
                      <td className="ims-muted">{act.authorized_by || "—"}</td>
                      <td className="ims-muted">{fmtTime(act.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {activities.length < activitiesTotal && (
                <div className="ims-activities-footer">
                  <button
                    className="ims-btn ims-btn--ghost ims-btn--sm"
                    onClick={() => {
                      const nextPage = activitiesPage + 1;
                      setActivitiesPage(nextPage);
                      loadActivities(nextPage * ACTIVITIES_LIMIT);
                    }}
                    disabled={activitiesLoading}
                  >
                    {activitiesLoading ? <Spinner dark /> : `Show More (${activitiesTotal - activities.length} remaining)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modals */}
        {modal === "createCat"   && <CreateCategoryModal onClose={closeModal} onDone={afterCreate} showToast={showToast} />}
        {modal === "editCat"     && selectedCat && <EditCategoryModal cat={selectedCat} onClose={closeModal} onDone={afterUpdate} showToast={showToast} />}
        {modal === "deleteCat"   && selectedCat && <DeleteModal cat={selectedCat} onClose={closeModal} onDone={afterDelete} showToast={showToast} />}
        {modal === "actionMenu"  && selectedCat && (
          <CategoryActionModal
            cat={selectedCat}
            onClose={closeModal}
            onOpenAddItem={() => openModal("addItem", selectedCat)}
            onOpenIntake={() => openModal("intake", selectedCat)}
            onOpenWorkspace={() => openModal("workspace", selectedCat)}
            onOpenWarehouse={() => {
              closeModal();
              setViewMode('warehouse');
              setSelectedCat(selectedCat);
            }}
          />
        )}
        {modal === "addItem"     && selectedCat && <AddItemModal cat={selectedCat} onClose={closeModal} onDone={afterItemAdd} showToast={showToast} />}
        {modal === "intake"      && selectedCat && <BulkStockIntakeModal cat={selectedCat} onClose={closeModal} onDone={afterMovement} showToast={showToast} />}
        {modal === "workspace"   && selectedCat && (
          <CategoryItemsModal
            cat={selectedCat}
            onClose={closeModal}
            onDone={refresh}
            showToast={showToast}
            onEditItem={(item) => {
              closeModal();
              setEditItem(item);
              setModal("editItem");
            }}
          />
        )}
        {modal === "editItem" && editItem && (
          <EditItemModal
            item={editItem}
            onClose={closeModal}
            onDone={afterItemUpdate}
            showToast={showToast}
          />
        )}

        {/* Toast */}
        {toast && <Toast msg={toast.msg} type={toast.type} />}
      </div>
    </div>
  );
};

export default InventoryDashboard;